// Copyright (c) Ping. All rights reserved.
//
// Builds the THREAD-scoped composer handler set (`ThreadComposerProps`)
// for the web thread panel. Every sender posts INTO the open thread by
// passing `parentId = <thread root content hash>` to the same send
// helpers the main composer uses — so attachments / voice / GIFs /
// stickers land as thread replies, not in the parent timeline.
//
// Poll / location / games go through the route-level overlays (which
// already exist in `ChatRouteHost`); the host sets a "pending thread
// target" before opening them and stamps `parent_id` on submit. Those
// three are passed in as `onOpenPoll` / `onOpenLocation` / `onOpenGames`.

import { type RefObject, useCallback, useMemo, useRef, useState } from 'react';

import {
  fileToImageSource,
  type GifItem,
  type StagedAttachment,
  type ThreadComposerProps,
} from '@ping/ui';

import { log } from '@core/log/log';

import { createKlipyGifProvider, createKlipyStickerProvider } from './klipyProvider';
import { resolveActiveConv } from './resolveActiveConv';
import { sendFileMessage } from './sendFileMessage';
import { sendGifMessage } from './sendGifMessage';
import { bundleForSource, sendImageMessage } from './sendImageMessage';
import { sendVoiceMessage } from './sendVoiceMessage';
import { WebVoiceRecorder } from './voiceRecorder';

export interface UseThreadComposerArgs {
  /** Live ref to the open thread's root content hash (null when closed). */
  readonly threadIdRef: RefObject<string | null>;
  /** Active conversation the thread lives in. */
  readonly conversationId: string | null;
  readonly conversationName?: string | undefined;
  readonly client: unknown;
  readonly mentionCandidates?: ThreadComposerProps['mentionCandidates'];
  /** Opens the route-level poll composer retargeted at the thread. */
  readonly onOpenPoll: () => void;
  readonly onOpenLocation: () => void;
  readonly onOpenGames: () => void;
  /** Opens the route-level meeting/event composer retargeted at the thread, so
   *  the create lands as a thread reply (`parent_id`) not a chatroom message. */
  readonly onOpenMeeting: () => void;
}

/** One-shot hidden file input → resolves the picked files. */
function pickFiles(accept: string): Promise<File[]> {
  return new Promise((resolve) => {
    if (typeof document === 'undefined') {
      resolve([]);
      return;
    }
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    if (accept.length > 0) input.accept = accept;
    input.style.display = 'none';
    let settled = false;
    const done = (files: File[]): void => {
      if (settled) return;
      settled = true;
      try {
        input.remove();
      } catch {
        /* best-effort */
      }
      resolve(files);
    };
    input.addEventListener('change', () => {
      done(input.files ? Array.from(input.files) : []);
    });
    // A cancelled picker fires `focus` on window without `change`; resolve
    // empty after a tick so the promise never dangles.
    window.addEventListener(
      'focus',
      () => {
        setTimeout(() => done([]), 400);
      },
      { once: true },
    );
    document.body.appendChild(input);
    input.click();
  });
}

export interface ThreadComposerBundle {
  /** Handlers spread onto the thread's <ChatRoomComposerRich>. */
  readonly composerProps: ThreadComposerProps;
  /** Staged image/file tray items shown above the composer. */
  readonly stagedAttachments: readonly StagedAttachment[];
  /** Remove one staged attachment (revokes its preview URL). */
  readonly onRemoveStagedAttachment: (id: string) => void;
  /** Send + clear all staged attachments (called on composer submit). */
  readonly onFlushStagedAttachments: () => Promise<void>;
}

function sizeLabel(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function newId(): string {
  return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `att-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function useThreadComposer(args: UseThreadComposerArgs): ThreadComposerBundle {
  const {
    threadIdRef,
    conversationId,
    conversationName,
    client,
    mentionCandidates,
    onOpenPoll,
    onOpenLocation,
    onOpenGames,
    onOpenMeeting,
  } = args;

  // Stable per-panel instances. Reused across thread opens.
  const recorderRef = useRef<WebVoiceRecorder | null>(null);
  if (recorderRef.current === null) recorderRef.current = new WebVoiceRecorder();
  const gifProviderRef = useRef(createKlipyGifProvider());
  const stickerProviderRef = useRef(createKlipyStickerProvider());

  // Staged image/file tray — picks accumulate here and send on submit
  // (mirrors the main chat). The raw `File`s live in a ref keyed by id.
  const [staged, setStaged] = useState<readonly StagedAttachment[]>([]);
  const filesByIdRef = useRef<Map<string, File>>(new Map());

  const resolveConv = useCallback(async () => {
    if (conversationId === null) return null;
    return resolveActiveConv({
      client: client as never,
      conversationId,
      ...(conversationName !== undefined ? { conversationName } : {}),
    });
  }, [client, conversationId, conversationName]);

  const stageFiles = useCallback((files: readonly File[]): void => {
    if (files.length === 0) return;
    const additions: StagedAttachment[] = [];
    for (const file of files) {
      const id = newId();
      filesByIdRef.current.set(id, file);
      const isImage = file.type.startsWith('image/');
      additions.push({
        id,
        kind: isImage ? 'image' : 'file',
        ...(isImage ? { previewUri: URL.createObjectURL(file) } : {}),
        ...(isImage ? {} : { name: file.name, sizeLabel: sizeLabel(file.size) }),
      });
    }
    setStaged((prev) => [...prev, ...additions].slice(0, 10));
  }, []);

  const onRemoveStagedAttachment = useCallback((id: string): void => {
    setStaged((prev) => {
      const hit = prev.find((a) => a.id === id);
      if (hit?.previewUri !== undefined) {
        try {
          URL.revokeObjectURL(hit.previewUri);
        } catch {
          /* best-effort */
        }
      }
      return prev.filter((a) => a.id !== id);
    });
    filesByIdRef.current.delete(id);
  }, []);

  const onFlushStagedAttachments = useCallback(async (): Promise<void> => {
    const parentId = threadIdRef.current;
    const items = staged;
    if (items.length === 0) return;
    // Optimistically clear the tray; revoke previews after send.
    setStaged([]);
    if (parentId === null || conversationId === null) return;
    const conv = await resolveConv();
    if (conv === null) return;
    for (const att of items) {
      const file = filesByIdRef.current.get(att.id);
      filesByIdRef.current.delete(att.id);
      if (file === undefined) continue;
      try {
        if (att.kind === 'image') {
          const source = await fileToImageSource(file);
          await sendImageMessage({
            conv,
            conversationId,
            bundle: bundleForSource(source),
            source,
            parentId,
          });
        } else {
          await sendFileMessage({ conv, conversationId, file, parentId });
        }
      } catch (err) {
        log.warn({ event: 'thread.attachment.send.failed', err: (err as Error).message });
      } finally {
        if (att.previewUri !== undefined) {
          try {
            URL.revokeObjectURL(att.previewUri);
          } catch {
            /* best-effort */
          }
        }
      }
    }
  }, [staged, threadIdRef, conversationId, resolveConv]);

  const composerProps = useMemo<ThreadComposerProps>(() => {
    const parent = (): string | null => threadIdRef.current;
    const sendGifOrSticker = async (gif: GifItem, isSticker: boolean): Promise<void> => {
      const parentId = parent();
      if (parentId === null || conversationId === null) return;
      const conv = await resolveConv();
      if (conv === null) return;
      try {
        await sendGifMessage({
          conv,
          conversationId,
          gif,
          parentId,
          ...(isSticker ? { isSticker: true } : {}),
        });
      } catch (err) {
        log.warn({
          event: isSticker ? 'thread.sticker.send.failed' : 'thread.gif.send.failed',
          err: (err as Error).message,
        });
      }
    };
    const sendGif = (gif: GifItem): Promise<void> => sendGifOrSticker(gif, false);
    const sendSticker = (gif: GifItem): Promise<void> => sendGifOrSticker(gif, true);
    const recorder = recorderRef.current;
    return {
      onPickImages: async () => {
        stageFiles(await pickFiles('image/*'));
      },
      onPickDoc: async () => {
        stageFiles(await pickFiles(''));
      },
      onStartVoice: () => {
        void recorder?.start().catch((err) => {
          log.warn({ event: 'thread.voice.start.failed', err: (err as Error).message });
        });
      },
      onSendVoiceMessage: async (durationMs: number) => {
        const parentId = parent();
        if (recorder === null || parentId === null || conversationId === null) return;
        try {
          const blob = await recorder.stop();
          if (blob.size === 0) return;
          const transcript = recorder.currentTranscript;
          const conv = await resolveConv();
          if (conv === null) return;
          await sendVoiceMessage({
            conv,
            conversationId,
            blob,
            durationMs,
            parentId,
            ...(transcript.length > 0 ? { transcript } : {}),
          });
        } catch (err) {
          log.warn({ event: 'thread.voice.send.failed', err: (err as Error).message });
        }
      },
      onCancelVoiceMessage: () => {
        recorder?.cancel();
      },
      subscribeAmplitudes: (listener: (amplitude: number) => void) =>
        recorder?.onAmplitude(listener) ?? (() => undefined),
      gifProvider: gifProviderRef.current,
      onSendGif: sendGif,
      stickerProvider: stickerProviderRef.current,
      onSendSticker: sendSticker,
      onOpenPoll,
      onOpenLocation,
      onOpenGames,
      onOpenEvent: onOpenMeeting,
      ...(mentionCandidates !== undefined ? { mentionCandidates } : {}),
    };
  }, [
    threadIdRef,
    conversationId,
    resolveConv,
    stageFiles,
    mentionCandidates,
    onOpenPoll,
    onOpenLocation,
    onOpenGames,
    onOpenMeeting,
  ]);

  return useMemo(
    () => ({ composerProps, stagedAttachments: staged, onRemoveStagedAttachment, onFlushStagedAttachments }),
    [composerProps, staged, onRemoveStagedAttachment, onFlushStagedAttachments],
  );
}
